import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import RoomsSection from './components/RoomsSection';
import GallerySection from './components/GallerySection';
import ReservationSection from './components/ReservationSection';
import AccessSection from './components/AccessSection';
import LuggageStorageSection from './components/LuggageStorageSection';
import OwnersBanner from './components/OwnersBanner';
import OverseasOwnersBanner from './components/OverseasOwnersBanner';
import ChildcareBanner from './components/ChildcareBanner';
import DrivingAssistantBanner from './components/DrivingAssistantBanner';
import IzakayaNightBanner from './components/IzakayaNightBanner';
import KaraokeNightBanner from './components/KaraokeNightBanner';
import NightComboBanner from './components/NightComboBanner';
import AirbnbLeadSection from './components/AirbnbLeadSection';
import SeoContentSection from './components/SeoContentSection';
import ServiceAreaSection from './components/ServiceAreaSection';
import CaseStudySection from './components/CaseStudySection';
import FaqSection from './components/FaqSection';
import TrustSection from './components/TrustSection';
import ComparisonSection from './components/ComparisonSection';
import AuthoritySection from './components/AuthoritySection';
import UrgencySection from './components/UrgencySection';
import TrustRiskSection from './components/TrustRiskSection';
import HowItWorksSection from './components/HowItWorksSection';
import ContactSection from './components/ContactSection';
import SnsSection from './components/SnsSection';
import InlineCtaBlock from '../../components/feature/InlineCtaBlock';
import GuideDownloadSection from './components/GuideDownloadSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function HomePage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language;
    const title = t('seo.home.title');
    const description = t('seo.home.description');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', t('seo.home.keywords'));
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/`);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    // Schema.org JSON-LD
    const schemaId = 'schema-home';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'LodgingBusiness',
        name: 'KOMINKA - Showa Retro House Tokyo',
        description: 'Experience authentic 1970s Showa-era Japan in our traditional kominka house near Haneda Airport. Tatami rooms, vintage furnishings, black rotary phones, chabudai low tables, retro posters, and nostalgic atmosphere transport you to a Japanese film scene.',
        url: `${SITE_URL}/`,
        image: 'https://public.readdy.ai/ai/img_res/891d736d0b09cadc5981307508a9e963.jpg',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1-12-12 Higashi-Magome',
          addressLocality: 'Ota City',
          addressRegion: 'Tokyo',
          postalCode: '143-0022',
          addressCountry: 'JP',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 35.5833,
          longitude: 139.7000,
        },
        telephone: '+81-3-XXXX-XXXX',
        priceRange: '¥¥',
        amenityFeature: [
          { '@type': 'LocationFeatureSpecification', name: 'Vintage 1970s Furnishings' },
          { '@type': 'LocationFeatureSpecification', name: 'Traditional Tatami Rooms' },
          { '@type': 'LocationFeatureSpecification', name: 'Shoji Screens' },
          { '@type': 'LocationFeatureSpecification', name: 'Black Rotary Phone' },
          { '@type': 'LocationFeatureSpecification', name: 'Chabudai Low Table' },
          { '@type': 'LocationFeatureSpecification', name: 'Retro Appliances' },
          { '@type': 'LocationFeatureSpecification', name: 'Wi-Fi' },
          { '@type': 'LocationFeatureSpecification', name: 'Air Conditioning' },
          { '@type': 'LocationFeatureSpecification', name: 'Full Kitchen' },
        ],
        checkinTime: '15:00',
        checkoutTime: '10:00',
        numberOfRooms: 1,
        petsAllowed: false,
        smokingAllowed: false,
        starRating: {
          '@type': 'Rating',
          ratingValue: '5',
        },
        sameAs: [
          'https://www.instagram.com/kominka_tokyo',
          'https://twitter.com/kominka_tokyo',
          'https://www.tiktok.com/@kominka_tokyo',
          'https://www.youtube.com/@kominka_tokyo',
          'https://www.facebook.com/kominka.tokyo',
          'https://line.me/R/ti/p/@kominka_tokyo',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'KOMINKA - Showa Retro House Tokyo',
        url: `${SITE_URL}/`,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ]);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen">
      <HeroSection />
      <AboutSection />
      <RoomsSection />
      <GallerySection />
      <ReservationSection />
      <AccessSection />
      <LuggageStorageSection />
      <OwnersBanner />
      <OverseasOwnersBanner />
      <ChildcareBanner />
      <DrivingAssistantBanner />
      <IzakayaNightBanner />
      <KaraokeNightBanner />
      <NightComboBanner />
      <AirbnbLeadSection />
      <UrgencySection />
      <HowItWorksSection />
      <SeoContentSection />
      <InlineCtaBlock />
      <GuideDownloadSection />
      <ServiceAreaSection />
      <CaseStudySection />
      <TrustRiskSection />
      <FaqSection />
      <TrustSection />
      <ComparisonSection />
      <AuthoritySection />
      <SnsSection />
      <ContactSection />
    </div>
  );
}